import { useState, useEffect, useRef } from "react";
import "./NewRequest.css";
import ArrowRight from "../../assets/icons/ArrowRight.svg";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { CircleAlert, MapPin, FileText, Phone, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Breadcrums from "../../components/breadcrums_v2/Breadcrums";
import ApiService from "../../components/Api_services";
import FallbackImg from "../../components/FallbackImg";
import CustomDropdown from "../../components/customdropdown_v2/CustomDropdown";
import MultiPurpose from "../../components/multipurpose_v2/MultiPurpose";

const NewRequestv2 = () => {
  // States for form data
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [itemCode, setItemCode] = useState("");
  const [itemId, setItemId] = useState("");
  const [itemFullName, setItemFullName] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [customerRemark, setCustomerRemark] = useState("");
  const [repairReasons, setRepairReasons] = useState({
    Dented: false,
    Scratches: false,
    "No Function": false,
    "Short Spare Part": false,
  });
  const [otherReason, setOtherReason] = useState("");
  const [activeType, setActiveType] = useState("stock");
  const [consumerName, setConsumerName] = useState("");
  const [consumerMobile, setConsumerMobile] = useState("");
  const [consumerAddress, setConsumerAddress] = useState("");
  const [showRequestNotif, setShowRequestNotif] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  const [itemPhoto, setItemPhoto] = useState("");
  const [itemCodeNotListed, setItemCodeNotListed] = useState(false);

  const itemCodeRef = useRef(null);
  const modalRef = useRef(null);
  // Form validation states
  const [validationErrors, setValidationErrors] = useState({
    branch: false,
    itemCode: false,
    referenceNo: false,
    repairReason: false,
    consumerName: false,
    consumerMobile: false,
    consumerAddress: false,
  });

  const navigate = useNavigate();

  const stockTypeOptions = [
    { value: "stock", label: "Item from stock" },
    { value: "consumer", label: "Item from consumer" },
  ];

  // Fetch branches when component mounts
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branches = await ApiService.getBranchList();
        const options = branches.map((branch) => ({
          value: branch.branch_id,
          label: branch.branch_code,
        }));
        setBranchOptions(options);
      } catch (error) {
        console.error("Error fetching branches:", error);
        // Show error to user if needed
      }
    };

    fetchBranches();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (itemCodeRef.current && !itemCodeRef.current.contains(event.target)) {
        setShowItemSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle item code search
  const handleItemCodeSearch = async (searchValue) => {
    setItemCode(searchValue);
    if (itemId) {
      setItemId("");
      setItemFullName("");
    }

    // Reset validation error
    setValidationErrors((prev) => ({ ...prev, itemCode: false }));

    if (searchValue.length >= 2) {
      try {
        const items = await ApiService.getItemCode(searchValue);
        setItemSuggestions(items);
        setShowItemSuggestions(items.length > 0);
      } catch (error) {
        console.error("Error searching for items:", error);
        setItemSuggestions([]);
        setShowItemSuggestions(false);
      }
    } else {
      setItemSuggestions([]);
      setShowItemSuggestions(false);
    }
  };

  // Select item from suggestions
  const selectItem = (item) => {
    setItemCode(item.item_code);
    setItemId(item.id);
    setItemFullName(item.display_text);
    setItemPhoto(item.display_photo || "/default-item.png");
    setShowItemSuggestions(false);

    // Reset validation error
    setValidationErrors((prev) => ({ ...prev, itemCode: false }));
  };

  // Handle repair reason checkboxes
  const handleRepairReasonChange = (reason) => {
    const newRepairReasons = {
      ...repairReasons,
      [reason]: !repairReasons[reason],
    };

    setRepairReasons(newRepairReasons);

    // Reset repair reason validation error if at least one is selected or other reason provided
    const hasSelectedReason = Object.values(newRepairReasons).some(
      (value) => value,
    );
    if (hasSelectedReason || otherReason) {
      setValidationErrors((prev) => ({ ...prev, repairReason: false }));
    }
  };

  // Handle other reason input
  const handleOtherReasonChange = (value) => {
    setOtherReason(value);

    // Reset repair reason validation error if value is not empty or at least one reason is checked
    const hasSelectedReason = Object.values(repairReasons).some(
      (value) => value,
    );
    if (value || hasSelectedReason) {
      setValidationErrors((prev) => ({ ...prev, repairReason: false }));
    }
  };

  // Handle branch selection
  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setValidationErrors((prev) => ({ ...prev, branch: false }));
  };

  // Handle reference number change
  const handleReferenceNoChange = (value) => {
    setReferenceNo(value);
    setValidationErrors((prev) => ({ ...prev, referenceNo: false }));
  };

  // Handle consumer name change
  const handleConsumerNameChange = (value) => {
    setConsumerName(value);
    if (activeType === "consumer") {
      setValidationErrors((prev) => ({ ...prev, consumerName: false }));
    }
  };

  // Handle consumer mobile change
  const handleConsumerMobileChange = (value) => {
    setConsumerMobile(value);
    if (activeType === "consumer") {
      setValidationErrors((prev) => ({ ...prev, consumerMobile: false }));
    }
  };

  // Handle consumer address change
  const handleConsumerAddressChange = (value) => {
    setConsumerAddress(value);
    if (activeType === "consumer") {
      setValidationErrors((prev) => ({ ...prev, consumerAddress: false }));
    }
  };

  // Handle item type selection
  const handleTypeChange = (type) => {
    setActiveType(type);

    // Reset consumer validation errors when switching back to stock
    if (type === "stock") {
      setValidationErrors((prev) => ({
        ...prev,
        consumerName: false,
        consumerMobile: false,
        consumerAddress: false,
      }));
    }
  };

  // Submit form
  const handleSubmit = async () => {
    setError("");

    // Check branch selection first
    if (!selectedBranch) {
      setValidationErrors((prev) => ({ ...prev, branch: true }));
      setError("Please select a branch code");
      return;
    }

    if (!itemCodeNotListed && (!itemCode || !itemId)) {
      setValidationErrors((prev) => ({ ...prev, itemCode: true }));
      setError("Please enter a item code");
      return;
    }

    // Rest of validation...
    const newValidationErrors = {
      branch: !selectedBranch,
      itemCode: !itemCode || !itemId, // 必须有itemCode和itemId
      // referenceNo: !referenceNo,
      // repairReason: false,
      // consumerName: false,
      // consumerMobile: false,
      // consumerAddress: false,
    };

    // Check repair reasons
    // const hasSelectedReason = Object.values(repairReasons).some(
    //   (value) => value
    // );
    // newValidationErrors.repairReason = !hasSelectedReason && !otherReason;

    // Consumer fields validation
    // if (activeType === "consumer") {
    //   newValidationErrors.consumerName = !consumerName;
    //   newValidationErrors.consumerMobile = !consumerMobile;
    //   newValidationErrors.consumerAddress = !consumerAddress;
    // }

    // setValidationErrors(newValidationErrors);

    // if (Object.values(newValidationErrors).some((isError) => isError)) {
    //   if (!itemId) {
    //     setError("Please select an item from the dropdown list");
    //   } else {
    //     setError("Please fill in all required fields");
    //   }
    //   return;
    // }

    setLoading(true);

    try {
      // Create form data using URLSearchParams
      const formData = new URLSearchParams();

      // Handle branch value - fix for '0' value
      const branchValue = selectedBranch.value.toString();
      const safeBranchValue = branchValue === "0" ? "0_" : branchValue;

      // Add all required fields - 使用选择的item code
      formData.append("post_item_code", itemCode);
      formData.append("branch", selectedBranch.value);
      formData.append(
        "chosen_type_set",
        activeType === "consumer" ? "consumer" : "stock",
      );
      // formData.append("support_ref_no", referenceNo);

      // Add repair reasons
      // const selectedReasons = Object.keys(repairReasons).filter(
      //   (key) => repairReasons[key]
      // );
      // if (selectedReasons.length > 0) {
      //   formData.append("repair_reason_array", selectedReasons.join(","));
      // }

      // Add other repair reason if provided
      // if (otherReason) {
      //   formData.append("other_repair_reason", otherReason);
      // }

      // Add consumer fields if needed
      // if (activeType === "consumer") {
      //   formData.append("support_consumer_name", consumerName);
      //   formData.append("support_consumer_tel", consumerMobile);
      //   formData.append("support_consumer_address", consumerAddress);
      // }

      if (customerRemark) {
        formData.append("customer_remark", customerRemark);
      }

      // console.log(
      //   "Submitting repair request with data:",
      //   Object.fromEntries(formData),
      // );

      const response = await ApiService.addNewRepair(formData);

      if (response && response.status === 200) {
        setNotifMessage("Repair request submitted successfully!");
        setShowRequestNotif(true);

        // Reset form
        setSelectedBranch(null);
        setItemCode("");
        setItemId("");
        setItemFullName("");
        setReferenceNo("");
        setRepairReasons({
          Dented: false,
          Scratches: false,
          "No Function": false,
          "Short Spare Part": false,
        });
        setOtherReason("");
        setActiveType("stock");
        setConsumerName("");
        setConsumerMobile("");
        setConsumerAddress("");
        setValidationErrors({
          branch: false,
          itemCode: false,
          referenceNo: false,
          repairReason: false,
          consumerName: false,
          consumerMobile: false,
          consumerAddress: false,
        });
      } else {
        const errorMessage =
          response?.message || "Failed to submit repair request";
        console.error("Error response:", response);
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Error submitting repair request:", error);

      // Special handling for 500 errors that might be successful submissions with post-processing errors
      if (error.response && error.response.status === 500) {
        // console.log("500 error - request may have succeeded anyway");
        setNotifMessage(
          "Your request may have been submitted successfully. Please check the repair list.",
        );
        setShowRequestNotif(true);
      } else {
        // Handle other errors
        let errorMessage = "An error occurred while submitting your request";

        if (error.response) {
          errorMessage =
            error.response.data?.message ||
            "Server returned an error: " + error.response.status;
        } else if (error.request) {
          errorMessage =
            "No response received from server. Please check your connection.";
        } else {
          errorMessage = error.message || errorMessage;
        }

        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="newrequest_v2">
      <Breadcrums />
      <div className="newrequest-content_v2">
        <div className="newrequest-title-container_v2">
          <div className="newrequest-title_v2">SERVICE REQUEST</div>
        </div>

        <div className="newrequest-main_v2">
          <div className="newrequest-main-left_v2">
            <div className="notice-box_v2">
              <CircleAlert size={20} color={"#e17100"} />

              <div>
                <div className="notice-title_v2">Important Notice</div>
                <div className="notice-text_v2">
                  Warranty is valid during the warranty period, additional
                  charges may apply after expiration.
                </div>
              </div>
            </div>

            <div className="service-request-card">
              <div className="service-request-card-header">
                Service Request Details
              </div>

              <div className="service-request-card-info">
                <div className="selectbranchcode_v2">
                  <div className="selectbranchcode-title_v2">
                    <MapPin size={16} color={"#283e24"} />
                    <span style={{ color: "red" }}>*</span>
                    Branch Code
                  </div>
                  <CustomDropdown
                    options={branchOptions}
                    value={selectedBranch ? selectedBranch.value : ""}
                    onChange={(val, opt) => {
                      handleBranchChange(opt);
                      setValidationErrors((prev) => ({
                        ...prev,
                        branch: false,
                      }));
                      setError("");
                    }}
                    placeholder="Select branch"
                    className={
                      validationErrors.branch
                        ? "newrequest-dropdown-error_v2"
                        : "newrequest-dropdown_v2"
                    }
                    popupRef={modalRef}
                  />
                </div>

                <div className="enteritemcode_v2">
                  <div className="enteritemcode-title_v2">
                    <FileText size={16} color={"#283e24"} />
                    <span style={{ color: "red" }}>*</span>
                    Product Item Code
                  </div>
                  <div
                    className="enteritemcode-input-container_v2"
                    ref={itemCodeRef}
                  >
                    <input
                      type="text"
                      placeholder="Enter the item code manually"
                      value={itemCode}
                      onChange={(e) => {
                        handleItemCodeSearch(e.target.value);
                        setValidationErrors((prev) => ({
                          ...prev,
                          itemCode: false,
                        }));
                        setError("");
                      }}
                      onFocus={() => {
                        if (itemCode.length >= 2) {
                          handleItemCodeSearch(itemCode);
                        }
                      }}
                      className={
                        validationErrors.itemCode
                          ? "enteritemcode-input-error_v2"
                          : "enteritemcode-input_v2"
                      }
                    />
                    <label className="enteritemcode-checkbox_v2">
                      <input
                        type="checkbox"
                        checked={itemCodeNotListed}
                        onChange={(e) => {
                          setItemCodeNotListed(e.target.checked);
                          if (e.target.checked) {
                            setItemCode("");
                            setItemId("");
                            setItemFullName("");
                            setItemPhoto("");
                          }
                        }}
                      />
                      Item code is not available
                    </label>
                    <FallbackImg
                      src={itemCodeNotListed ? "" : itemPhoto}
                      alt={itemCode}
                      style={{
                        width: "48px",
                        height: "48px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: " 2px solid #e5e5e5",
                        background: "#ffffff",
                        boxSizing: "border-box",
                      }}
                    />

                    {showItemSuggestions &&
                      itemSuggestions.length > 0 &&
                      !itemCodeNotListed && (
                        <div className="item-suggestions_v2">
                          {itemSuggestions.map((item) => (
                            <div
                              key={item.id}
                              className="item-suggestion_v2"
                              onClick={() => selectItem(item)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "15px",
                              }}
                            >
                              {/* 左侧图片 */}
                              <FallbackImg
                                src={item.display_photo}
                                alt={item.item_code}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  background: "#f5f5f5",
                                }}
                              />
                              {/* 右侧文字 */}
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <span className="item-code_v2">
                                  {item.item_code}
                                </span>
                                <span className="item-name_v2">
                                  {item.nick_name}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>

                <div className="selectstocktype_v2">
                  <div className="selectstocktype-title_v2">
                    <span style={{ color: "red" }}>*</span>
                    Item Source
                  </div>
                  <div className="selectstocktype-subtitle_v2">
                    Please specify whether this item is from your stock
                    inventory or belongs to the customer
                  </div>
                  <div className="stock-type-buttons_v2">
                    {stockTypeOptions.map((opt) => (
                      <button
                        type="button"
                        key={opt.value}
                        className={
                          activeType === opt.value
                            ? "stock-type-btn_v2 active"
                            : "stock-type-btn_v2"
                        }
                        onClick={() => handleTypeChange(opt.value)}
                        aria-pressed={activeType === opt.value}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="entercustremark_v2">
                  <div className="entercustremark-title_v2">
                    Additional Notes (Optional)
                  </div>
                  <textarea
                    className="entercustremark-input_v2"
                    placeholder="Please provide any additional information such as consumer reference number, contact details, fault description, or special requirements..."
                    value={customerRemark}
                    rows="5"
                    onChange={(e) => setCustomerRemark(e.target.value)}
                  />
                </div>

                {error && (
                  <div style={{ color: "red", marginTop: 10 }}>{error}</div>
                )}

                <div className="form-actions_v2">
                  {/* <button
                    className="btn-cancel_v2"
                    onClick={() => {
                      // simple reset behavior for cancel
                      setSelectedBranch(null);
                      setItemCode("");
                      setItemId("");
                      setItemFullName("");
                      setReferenceNo("");
                      setCustomerRemark("");
                    }}
                  >
                    Cancel
                  </button> */}

                  <button
                    className="btn-primary_v2"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Service Request"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="newrequest-main-right_v2">
            <div className="process-card_v2">
              <div className="process-title_v2">
                <CircleAlert size={20} color={"#1c398e"} />
                Service Request Process
              </div>
              <ol className="process-list_v2">
                <li>Fill in the form with accurate product details</li>
                <li>Submit your service request for review</li>
                <li>Our team will contact you shortly</li>
              </ol>
            </div>

            <div className="support-card_v2">
              <div className="support-title_v2">Customer Support</div>
              <div className="support-text_v2">
                For assistance or inquiries regarding your service request,
                please contact our customer support team.
              </div>
              <div className="support-contact_v2">
                <div>
                  <div className="support-icon_v2">
                    <Phone size={16} />
                  </div>
                  017-3750336
                </div>
                <div>
                  <div className="support-icon_v2">
                    <Mail size={16} />
                  </div>
                  crm@unity.net.my
                </div>
              </div>
            </div>
          </div>
        </div>

        {showRequestNotif && (
          <MultiPurpose
            title="Request Submitted"
            message="Your repair request has been submitted successfully. Our team will review and respond shortly."
            yesText="Noted"
            hideNo={true}
            onYes={() => {
              setShowRequestNotif(false);
              window.location.reload();
            }}
            onClose={() => {
              setShowRequestNotif(false);
              window.location.reload();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default NewRequestv2;
